import { router } from '@inertiajs/react'
import PageHeader from '@/salesminer/layouts/app/PageHeader.tsx'
import type { SalesminerAccount } from '@/types/serializers'
import SalesminerAppLayout from "@/salesminer/layouts/SalesminerAppLayout.tsx";

interface Tab {
    label: string
    id: string
    route: string
}

interface AccountLayoutProps {
    account: SalesminerAccount
    activeTab: string
    children: React.ReactNode
}

/**
 * A layout shared by the individual page components used to display the tabs. That way we can treat each tab as a distinct page in routing
 * while maintaining the same simple page layout exposing only the actual tab that changes.
 *
 * E.g.
 * <AccountLayout account={account} activeTab="competitive">
 *     <CompetitiveAnalysisTab account={account}/>
 *  </AccountLayout>
 */
export default function AccountLayout({account, activeTab, children}: AccountLayoutProps) {
    // Extract organization slug from current URL
    const organizationSlug = window.location.pathname.split('/')[2]

    const tabs: Tab[] = [
        {label: 'Overview', id: 'overview', route: `/~/${organizationSlug}/accounts/${account.id}`},
        {
            label: 'Opportunities',
            id: 'opportunities',
            route: `/~/${organizationSlug}/accounts/${account.id}/opportunities`
        },
        {
            label: 'Competitive Analysis',
            id: 'competitive',
            route: `/~/${organizationSlug}/accounts/${account.id}/competitive`
        },
        {label: 'Details', id: 'details', route: `/~/${organizationSlug}/accounts/${account.id}/details`}
    ]

    const handleBack = () => {
        router.visit(`/~/${organizationSlug}/accounts`)
    }

    const handleTabClick = (route: string) => {
        router.visit(route)
    }

    return (
        <SalesminerAppLayout>
            <div className="p-6">
                <div className="max-w-[1400px] mx-auto">
                    <PageHeader
                        title={account.name}
                        subtitle="Client Details and Analytics"
                        backLink={{
                            onClick: handleBack,
                            label: 'Back'
                        }}
                    />

                    {/* Tab Navigation */}
                    <div className="border-b border-slate-200 dark:border-slate-700 mb-6">
                        <nav className="-mb-px flex space-x-8">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabClick(tab.route)}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                        activeTab === tab.id
                                            ? 'border-accent text-primary'
                                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Tab Content */}
                    {children}
                </div>
            </div>
        </SalesminerAppLayout>
    )
}